import {
  Address,
  CreateOrderRequest,
  Experience,
  ExperienceOption,
} from '@/types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { FormikErrors } from 'formik'
import LocationItem from './location-item'
import { useRouter } from 'next/router'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'

const ExperiencePickupField = ({
  experience,
  selectedOption,
  values,
  setFieldValue,
  actionable,
}: {
  experience: Experience
  selectedOption?: ExperienceOption
  values: CreateOrderRequest
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<CreateOrderRequest>>
  actionable: boolean
}) => {
  const { t } = useTranslation('experience')
  const router = useRouter()

  // Combine experience-level picking points with option-level meeting point
  const getInitialPoints = (): Address[] => {
    return [
      ...(experience?.pickingPoints ?? []),
      ...(selectedOption?.meetingPoint ? [selectedOption.meetingPoint] : []),
    ].filter((p) => p?.formattedAddress)
  }

  const [points, setPoints] = useState<Address[]>(getInitialPoints())

  // Update points when selected option changes
  useEffect(() => {
    setPoints(getInitialPoints())
  }, [selectedOption?.id])

  // Auto-select when there's only one location
  useEffect(() => {
    if (points.length === 1 && !values.selectedPoint) {
      setFieldValue('selectedPoint', points[0])
    }
  }, [points, values.selectedPoint, setFieldValue])

  // Get meeting note and allowCustomPickingPoint from option (with fallback to experience for backwards compatibility)
  const meetingNote = selectedOption?.meetingNote ?? experience?.meetingNote
  const allowCustomPickingPoint =
    selectedOption?.allowCustomPickingPoint ?? experience?.allowCustomPickingPoint

  if (points.length === 0 && !meetingNote && !allowCustomPickingPoint) {
    return null
  }

  return (
    <>
      <Separator />

      <fieldset>
        <Label>{t('meeting_and_pickup')}</Label>
        {points.length === 1 && actionable ? (
          <p className="text-sm text-muted-foreground">{points[0].formattedAddress}</p>
        ) : points.length > 1 && actionable ? (
          <Select
            value={values.selectedPoint?.formattedAddress}
            required
            onValueChange={(value) => {
              setFieldValue(
                'selectedPoint',
                points.find((p) => p.formattedAddress === value)
              )
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('select_your_pickup_point')} />
            </SelectTrigger>
            <SelectContent>
              {points.map((point, i) => (
                <SelectItem value={point.formattedAddress ?? ''} key={i}>
                  {point.formattedAddress}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        {allowCustomPickingPoint && actionable ? (
          <LocationItem
            locale={router.locale}
            location={values.selectedPoint}
            removeOnly
            handleRemove={() => setFieldValue('selectedPoint', undefined)}
            placeholder={t('type_your_pickup_address')}
            onChange={(value) => {
              setPoints([...points, value])

              setTimeout(() => {
                setFieldValue('selectedPoint', value)
              }, 100)
            }}
          />
        ) : null}

        {meetingNote && (
          <div className="mt-4">
            <p className="text-sm text-gray-500">{meetingNote}</p>
          </div>
        )}
      </fieldset>
    </>
  )
}

export default ExperiencePickupField
